import { normalizeHex, type DesignArtworkLayer } from '@pz/scene';
import type { ArtworkItem, ArtworkLibrary } from '../designer-types.js';
import {
  chipRow,
  el,
  field,
  hexOf,
  note,
  searchBox,
  swatchGrid,
  switchRow,
  tabs,
  textBtn,
  type DesignerCtx,
} from './panels.js';
import { canAddLayer, freshArtworkLayer, type LayerPatch } from './state.js';
import { run } from './commands.js';
import { fillText } from './texts.js';

/**
 * Ukrasi and Emoji: one panel, two tabs' worth of catalogue.
 *
 * The headline is RECOLOURING. Every competitor's clipart is take-it-or-leave-
 * it; a shopper who wants the heart in the shop's pink has to ask for a new
 * SVG. Here a vector arrives with its distinct fills listed (`ArtworkItem.colors`
 * — the API computes them, at most eight, most used first) and each one gets a
 * chip: tap it, pick a colour, and `colorMap` carries the swap all the way to
 * the print file. "Jedna boja" flattens the whole thing to one ink, which is
 * what a single-colour transfer needs.
 *
 * The grid pages in sixties and keeps what it fetched while the designer is
 * open, because the panel is rebuilt on every selection change and an emoji
 * pack is 1500 items — refetching them per tap would be a new request every
 * time a shopper nudges a layer.
 */

interface GridView {
  libraryId?: string;
  q: string;
  items: ArtworkItem[];
  total: number;
  loading: boolean;
  request?: number;
  failed: boolean;
}

interface ArtworkCache {
  libraries?: ArtworkLibrary[];
  loadingLibraries: boolean;
  librariesPromise?: Promise<ArtworkLibrary[]>;
  librariesFailed?: boolean;
  /** Asset id → the artwork's own distinct fills, for the recolour chips. */
  colors: Map<string, string[]>;
  views: { artwork: GridView; emoji: GridView };
  /** Which recolour chip is expanded, per layer. */
  editing?: { layerId: string; from: string };
}

const caches = new WeakMap<DesignerCtx, ArtworkCache>();

function cacheFor(ctx: DesignerCtx): ArtworkCache {
  const existing = caches.get(ctx);
  if (existing) return existing;
  const fresh: ArtworkCache = {
    loadingLibraries: false,
    colors: new Map(),
    views: {
      artwork: { q: '', items: [], total: 0, loading: false, failed: false },
      emoji: { q: '', items: [], total: 0, loading: false, failed: false },
    },
  };
  caches.set(ctx, fresh);
  return fresh;
}

const PAGE = 60;

/**
 * `icons` is the desktop rail's single "Ikone" panel: every library the zone
 * allows, emoji included, with the tab strip to narrow it. `artwork` and
 * `emoji` are the older split, kept because the phone's Add drawer has a tab
 * for each.
 */
export type ArtworkKind = 'artwork' | 'emoji' | 'icons';

export function renderArtworkPanel(body: HTMLElement, ctx: DesignerCtx, kind: ArtworkKind): () => void {
  const cfg = ctx.config;
  const cache = cacheFor(ctx);
  const view = cache.views[kind === 'emoji' ? 'emoji' : 'artwork'];
  const cleanups: Array<() => void> = [];
  let alive = true;
  let ownRequest: number | undefined;
  cleanups.push(() => {
    alive = false;
    if (ownRequest !== undefined && view.request === ownRequest) { view.request++; view.loading = false; }
  });

  const selected = ctx.selected();
  const artwork = selected?.kind === 'artwork' ? selected : undefined;

  // ── The selected artwork's own controls, first ──────────────────────────
  if (artwork) body.appendChild(recolourSection(ctx, cache, artwork));

  // ── Libraries ───────────────────────────────────────────────────────────
  const tabRow = el('div');
  const grid = el('div', 'pzd-grid');
  const status = el('div', 'pzd-hint');
  status.setAttribute('role', 'status');
  const count = el('div', 'pzd-hint');
  const retry = textBtn(ctx.texts.artworkRetry, () => { void loadLibraries(); void fetchPage(true); });
  retry.hidden = true;
  const sentinel = el('div', 'pzd-sentinel');
  const more = textBtn(ctx.texts.more, () => void fetchPage(false), { className: 'pzd-more' });
  more.hidden = true;

  const mine = (libraries: ArtworkLibrary[]): ArtworkLibrary[] => {
    if (kind === 'icons') return libraries;
    return libraries.filter((library) => (kind === 'emoji' ? library.kind === 'emoji' : library.kind !== 'emoji'));
  };

  function paintTabs(): void {
    tabRow.textContent = '';
    const list = mine(cache.libraries ?? []);
    if (list.length <= 1) return;
    tabRow.appendChild(tabs([
      { id: '', label: ctx.texts.allLibraries, on: !view.libraryId },
      ...list.map((library) => ({ id: library.id, label: library.name, on: view.libraryId === library.id })),
    ], (id) => {
      view.libraryId = id || undefined;
      view.items = [];
      view.total = 0;
      void fetchPage(true);
    }));
  }

  /**
   * What the "Sve" tab shows.
   *
   * The catalogue route answers with every library the zone allows, emoji
   * included (that is what the spec's union means), so the tab has to drop
   * the other tab's libraries itself — a heart in the emoji tab and a smiley
   * under Ukrasi is exactly the muddle the two tabs exist to avoid.
   */
  function visible(): ArtworkItem[] {
    if (view.libraryId || !cache.libraries) return view.items;
    const allowed = new Set(mine(cache.libraries).map((library) => library.id));
    if (!allowed.size) return view.items;
    return view.items.filter((item) => allowed.has(item.libraryId));
  }

  function paintGrid(): void {
    grid.textContent = '';
    for (const item of visible()) {
      const tile = el('button', 'pzd-tile');
      tile.type = 'button';
      tile.setAttribute('aria-label', item.name);
      tile.title = item.name;
      const img = el('img');
      img.src = item.url;
      img.alt = '';
      img.loading = 'lazy';
      tile.appendChild(img);
      tile.addEventListener('click', () => add(item));
      grid.appendChild(tile);
    }
    status.textContent = view.loading
      ? ctx.texts.loading
      : view.failed || cache.librariesFailed
        ? ctx.texts.artworkFailed
        : visible().length === 0 ? ctx.texts.noResults : '';
    more.hidden = view.loading || view.items.length >= view.total;
    retry.hidden = view.loading || !(view.failed || cache.librariesFailed);
    const list = mine(cache.libraries ?? []);
    const offered = (view.libraryId ? list.filter(library => library.id === view.libraryId) : list).reduce((sum, library) => sum + library.count, 0);
    count.hidden = !offered;
    count.textContent = fillText(ctx.texts.artworkCount, { n: offered.toLocaleString('sr-RS') });
  }

  function add(item: ArtworkItem): void {
    const room = canAddLayer(ctx.store.state(), cfg, 'artwork', { emoji: kind === 'emoji' });
    if (!room.ok) {
      ctx.toast(fillText(ctx.texts.maxLayers, { n: room.limit ?? cfg.maxLayers }), 'error');
      return;
    }
    ctx.renderer.registerAsset(item.id, item.url);
    if (item.colors?.length) cache.colors.set(item.id, item.colors.map(normalizeHex).filter(Boolean));
    ctx.dispatch({
      type: 'add',
      layer: freshArtworkLayer(ctx.rawConfig, {
        assetId: item.id,
        libraryId: item.libraryId,
        vector: item.vector,
        ...(item.w ? { w: item.w } : {}),
        ...(item.h ? { h: item.h } : {}),
      }, ctx.addCount()),
    });
    ctx.refreshPanel();
  }

  async function fetchPage(reset: boolean): Promise<void> {
    if (view.loading && !reset) return;
    const request = (view.request ?? 0) + 1;
    view.request = ownRequest = request;
    view.loading = true;
    view.failed = false;
    if (reset) {
      view.items = [];
      view.total = 0;
    }
    paintGrid();
    try {
      const page = await ctx.artwork.search({
        ...(view.libraryId ? { libraryId: view.libraryId } : {}),
        ...(view.q ? { q: view.q } : {}),
        offset: view.items.length,
        limit: PAGE,
      });
      if (!alive || view.request !== request) return;
      const seen = new Set(view.items.map((item) => item.id));
      for (const item of page.items) {
        if (seen.has(item.id)) continue;
        view.items.push(item);
        if (item.colors?.length) cache.colors.set(item.id, item.colors.map(normalizeHex).filter(Boolean));
      }
      view.total = Math.max(page.total, view.items.length);
    } catch {
      if (!alive || view.request !== request) return;
      view.failed = true;
    } finally {
      if (alive && view.request === request) {
        view.loading = false;
        paintGrid();
      }
    }
  }

  async function loadLibraries(): Promise<void> {
    try {
      cache.librariesPromise ??= ctx.artwork.libraries();
      cache.libraries = await cache.librariesPromise;
      cache.librariesFailed = false;
    } catch {
      cache.librariesPromise = undefined;
      cache.libraries = [];
      cache.librariesFailed = true;
    }
    if (alive) { paintTabs(); paintGrid(); }
  }

  const searchRow = searchBox(ctx.texts.search, view.q, (q) => {
    view.q = q;
    void fetchPage(true);
  });

  const themes = chipRow([
    ['heart', 'Srca'], ['flower', 'Cveće'], ['dog', 'Psi'], ['cat', 'Mačke'],
    ['star', 'Zvezde'], ['gift', 'Pokloni'], ['wedding', 'Venčanje'], ['sport', 'Sport'],
    ['food', 'Hrana'], ['car', 'Vozila'], ['smile', 'Osmesi'],
  ].map(([id, label]) => ({ id: id!, label: label!, on: view.q === id })), q => {
    view.q = view.q === q ? '' : q;
    const input = searchRow.querySelector('input'); if (input) input.value = view.q;
    const ids = ['heart','flower','dog','cat','star','gift','wedding','sport','food','car','smile'];
    [...themes.querySelectorAll('button')].forEach((button, index) => { const on = ids[index] === view.q; button.classList.toggle('is-on', on); button.setAttribute('aria-pressed', String(on)); });
    void fetchPage(true);
  });
  themes.setAttribute('aria-label', 'Teme ukrasa');
  const box = field(kind === 'emoji' ? ctx.texts.addEmoji : kind === 'icons' ? ctx.texts.toolIcons : ctx.texts.addArtwork);
  box.wrap.append(count, searchRow, tabRow, themes, grid, status, retry, more, sentinel);
  body.appendChild(box.wrap);

  paintTabs();
  paintGrid();
  void loadLibraries();
  if (!view.items.length && !view.loading) void fetchPage(true);

  if (typeof IntersectionObserver === 'function') {
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      if (view.loading || view.items.length === 0 || view.items.length >= view.total) return;
      void fetchPage(false);
    }, { root: body, rootMargin: '200px' });
    observer.observe(sentinel);
    cleanups.push(() => observer.disconnect());
  }

  return () => {
    for (const stop of cleanups) stop();
  };
}

/** Recolour, flip, fade, duplicate, delete — for the artwork now selected. */
function recolourSection(ctx: DesignerCtx, cache: ArtworkCache, layer: DesignArtworkLayer): HTMLElement {
  const cfg = ctx.config;
  const id = layer.id;
  const wrap = el('div');
  const patch = (changes: LayerPatch, tag?: string): void => {
    ctx.dispatch({ type: 'update', id, patch: changes, ...(tag ? { tag } : {}) });
  };

  if (cfg.artwork.allowRecolor) {
    const box = field(ctx.texts.recolor);
    if (layer.vector) {
      box.wrap.appendChild(switchRow(ctx.texts.singleColor, Boolean(layer.singleColor), (on) => {
        patch({ singleColor: on ? cfg.text.palette[0] ?? { hex: '#000000' } : undefined });
      }));
      if (layer.singleColor) {
        box.wrap.appendChild(swatchGrid({
          colors: cfg.text.palette,
          selected: layer.singleColor,
          onPick: (color) => patch({ singleColor: color }),
          ...(cfg.text.allowCustomColor ? { custom: { label: ctx.texts.customColor } } : {}),
        }));
      } else {
        const originals = cache.colors.get(layer.assetId) ?? (layer.colorMap ?? []).map((entry) => entry.from);
        if (!originals.length) {
          box.wrap.appendChild(el('div', 'pzd-hint', ctx.texts.originalColors));
        } else {
          const active = cache.editing?.layerId === id ? cache.editing.from : undefined;
          box.wrap.appendChild(chipRow(originals.map((from) => ({
            id: from,
            label: ' ',
            ariaLabel: `${ctx.texts.recolor} ${from}`,
            on: from === active,
          })), (from) => {
            cache.editing = cache.editing?.from === from && cache.editing.layerId === id
              ? undefined
              : { layerId: id, from };
            ctx.refreshPanel();
          }));
          // The chips are colour dots: paint them after the row exists so the
          // generic chip builder stays generic.
          const chips = box.wrap.querySelectorAll('.pzd-chip-btn');
          originals.forEach((from, index) => {
            const chip = chips[index] as HTMLElement | undefined;
            if (!chip) return;
            const mapped = layer.colorMap?.find((entry) => entry.from === from)?.color;
            chip.style.background = hexOf(mapped) || from;
            chip.style.minWidth = '34px';
            chip.style.minHeight = '28px';
            if (mapped) chip.style.borderColor = 'var(--pz-accent, #5145cd)';
          });
          if (active) {
            box.wrap.appendChild(swatchGrid({
              colors: cfg.text.palette,
              selected: layer.colorMap?.find((entry) => entry.from === active)?.color,
              onPick: (color) => {
                const rest = (layer.colorMap ?? []).filter((entry) => entry.from !== active);
                patch({ colorMap: [...rest, { from: active, color }] });
              },
              none: {
                label: ctx.texts.originalColors,
                onPick: () => {
                  const rest = (layer.colorMap ?? []).filter((entry) => entry.from !== active);
                  patch({ colorMap: rest.length ? rest : undefined });
                },
              },
              ...(cfg.text.allowCustomColor ? { custom: { label: ctx.texts.customColor } } : {}),
            }));
          }
        }
      }
    } else {
      box.wrap.appendChild(swatchGrid({
        colors: cfg.text.palette,
        selected: layer.colorize,
        onPick: (color) => patch({ colorize: color }),
        none: { label: ctx.texts.originalColors, onPick: () => patch({ colorize: undefined }) },
        ...(cfg.text.allowCustomColor ? { custom: { label: ctx.texts.tint } } : {}),
      }));
    }
    wrap.appendChild(box.wrap);
  } else {
    wrap.appendChild(note(ctx.texts.originalColors));
  }

  // Opacity, flips and the rest of the shared controls live in Efekti and the
  // contextual toolbar; this panel keeps only what is specific to artwork.
  wrap.appendChild(chipRow([
    { id: 'flipX', label: ctx.texts.flip, icon: 'flipH', on: Boolean(layer.flipX) },
    { id: 'duplicate', label: ctx.texts.duplicate, icon: 'copy' },
    { id: 'remove', label: ctx.texts.remove, icon: 'trash' },
  ], (action) => {
    if (action === 'flipX') run(ctx, { op: 'flip', ids: [id], axis: 'x' });
    if (action === 'duplicate') run(ctx, { op: 'duplicate', ids: [id] });
    if (action === 'remove') run(ctx, { op: 'delete', ids: [id] });
  }));

  return wrap;
}
